import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Users } from "lucide-react";
import { api } from "@/lib/api";
import { EVENT_TYPES, MENU_CATEGORIES } from "@/data/site";
import Seo from "@/components/Seo";

const CAT_STEPS = MENU_CATEGORIES;

export default function BuildMenu() {
  const nav = useNavigate();
  const [menu, setMenu] = useState([]);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [eventType, setEventType] = useState("");
  const [guests, setGuests] = useState(25);
  const [selected, setSelected] = useState({});
  const [requests, setRequests] = useState("");

  useEffect(() => { api.get("/menu").then((r) => setMenu(r.data)).catch(() => {}); }, []);

  const byCat = (c) => menu.filter((m) => m.category === c);
  const toggle = (item) => setSelected((s) => {
    const arr = s[item.category] || [];
    const exists = arr.find((x) => x.id === item.id);
    return { ...s, [item.category]: exists ? arr.filter((x) => x.id !== item.id) : [...arr, item] };
  });
  const isSel = (item) => (selected[item.category] || []).some((x) => x.id === item.id);

  const perPerson = useMemo(() => Object.values(selected).flat().reduce((a, b) => a + (b.price || 0), 0), [selected]);
  const total = perPerson * (guests || 0);

  const steps = ["Event Type", "Guests", ...CAT_STEPS, "Special Requests", "Summary"];
  const totalSteps = steps.length;
  const go = (d) => { setDir(d); setStep((s) => Math.min(Math.max(s + d, 0), totalSteps - 1)); };

  const canNext = () => {
    if (step === 0) return !!eventType;
    if (step === 1) return guests >= 1;
    return true;
  };

  const proceedToQuote = () => {
    const flatMenu = {};
    Object.entries(selected).forEach(([k, v]) => { flatMenu[k] = v.map((i) => i.name); });
    localStorage.setItem("ko_build", JSON.stringify({ eventType, guests, menu: flatMenu, perPerson, total, requests }));
    nav("/request-a-quote");
  };

  const variants = { enter: (d) => ({ opacity: 0, x: d * 40 }), center: { opacity: 1, x: 0 }, exit: (d) => ({ opacity: 0, x: -d * 40 }) };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="build-menu-page">
      <Seo
        title="Build Your Own Catering Menu | K.O Chef & Catering SW Florida"
        description="Design a fully custom catering menu for your event in Southwest Florida. Choose appetizers, salads, mains, sides, desserts and drinks and get an instant price estimate."
        path="/build-your-menu"
      />
      <div className="container-lux max-w-4xl">
        <div className="text-center mb-10">
          <p className="overline mb-4">Build Your Own Menu</p>
          <h1 className="text-4xl md:text-5xl font-light text-white">Your event, your menu.</h1>
        </div>

        {/* progress */}
        <div className="flex items-center gap-1 mb-4">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 transition-colors ${i <= step ? "bg-gold" : "bg-white/10"}`} />
          ))}
        </div>
        <p className="text-center text-xs uppercase tracking-widest text-white/40 mb-12">Step {step + 1} of {totalSteps} — {steps[step]}</p>

        <div className="min-h-[360px]">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div key={step} custom={dir} variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.4 }}>
              {step === 0 && (
                <div data-testid="step-event-type">
                  <h2 className="text-2xl font-light text-white text-center mb-8">What are we celebrating?</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {EVENT_TYPES.map((t) => (
                      <button key={t} onClick={() => setEventType(t)} data-testid={`event-${t.replace(/\s+/g,"-")}`}
                        className={`py-5 border text-sm uppercase tracking-wider transition-colors ${eventType === t ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/60 hover:border-gold/50"}`}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="text-center" data-testid="step-guests">
                  <h2 className="text-2xl font-light text-white mb-10">How many guests?</h2>
                  <div className="flex items-center justify-center gap-6">
                    <button onClick={() => setGuests((g) => Math.max(1, g - 5))} className="w-12 h-12 border border-white/15 text-gold text-2xl hover:border-gold" data-testid="guests-minus">−</button>
                    <div className="flex items-center gap-3"><Users className="w-6 h-6 text-gold" /><input type="number" value={guests} onChange={(e) => setGuests(Math.max(1, +e.target.value))} className="w-28 text-center bg-transparent text-5xl font-serif text-white border-b border-white/20 focus:border-gold outline-none" data-testid="guests-input" /></div>
                    <button onClick={() => setGuests((g) => g + 5)} className="w-12 h-12 border border-white/15 text-gold text-2xl hover:border-gold" data-testid="guests-plus">+</button>
                  </div>
                  <input type="range" min="5" max="300" step="5" value={guests} onChange={(e) => setGuests(+e.target.value)} className="w-full max-w-md mt-10 accent-[#D4AF37]" data-testid="guests-slider" />
                </div>
              )}
              {CAT_STEPS.includes(steps[step]) && (
                <div data-testid={`step-${steps[step].replace(/\s+/g,"-").toLowerCase()}`}>
                  <h2 className="text-2xl font-light text-white text-center mb-2">Select your {steps[step]}</h2>
                  <p className="text-center text-white/40 text-sm mb-8">Choose as many as you like · prices are per person</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {byCat(steps[step]).map((item) => (
                      <button key={item.id} onClick={() => toggle(item)} data-testid={`item-${item.id}`}
                        className={`flex items-center justify-between text-left p-5 border transition-colors ${isSel(item) ? "border-gold bg-gold/10" : "border-white/15 hover:border-gold/50"}`}>
                        <div><p className="text-white">{item.name}</p><p className="text-white/45 text-xs mt-1">{item.description}</p></div>
                        <div className="flex items-center gap-3 shrink-0 ml-4"><span className="text-gold text-sm">${item.price}</span>{isSel(item) && <Check className="w-5 h-5 text-gold" />}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {steps[step] === "Special Requests" && (
                <div data-testid="step-requests">
                  <h2 className="text-2xl font-light text-white text-center mb-8">Any special requests?</h2>
                  <textarea value={requests} onChange={(e) => setRequests(e.target.value)} rows={6} placeholder="Dietary restrictions, allergies, theme, service style…" className="lux-input resize-none max-w-2xl mx-auto block" data-testid="requests-input" />
                </div>
              )}
              {steps[step] === "Summary" && (
                <div data-testid="step-summary">
                  <h2 className="text-2xl font-light text-white text-center mb-8">Your custom menu</h2>
                  <div className="border border-white/10 p-8 space-y-4 max-w-2xl mx-auto">
                    <div className="flex justify-between text-sm"><span className="text-white/50">Event Type</span><span className="text-white">{eventType || "—"}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-white/50">Guests</span><span className="text-white">{guests}</span></div>
                    {CAT_STEPS.map((c) => (selected[c]?.length ? (
                      <div key={c} className="flex justify-between text-sm gap-6"><span className="text-white/50 shrink-0">{c}</span><span className="text-white text-right">{selected[c].map((i) => i.name).join(", ")}</span></div>
                    ) : null))}
                    <div className="border-t border-white/10 pt-4 flex justify-between"><span className="text-white/50 text-sm">Per Person (Food)</span><span className="text-gold">${perPerson.toFixed(2)}</span></div>
                    <div className="flex justify-between items-center"><span className="text-white">Food & Menu Subtotal</span><span className="text-3xl font-serif text-gold" data-testid="estimated-total">${total.toFixed(2)}</span></div>
                  </div>
                  <p className="text-center text-white/40 text-xs mt-4 max-w-lg mx-auto">This is the food & menu subtotal. Your Catering Service Fee and Sales Tax are added on the quote page.</p>
                  <div className="text-center mt-8"><button onClick={proceedToQuote} className="btn-gold" data-testid="build-request-quote">Request Quote With This Menu <ArrowRight className="w-4 h-4" /></button></div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* running estimate + nav */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-white/10">
          <button onClick={() => go(-1)} disabled={step === 0} className="flex items-center gap-2 text-sm uppercase tracking-widest text-white/60 hover:text-gold disabled:opacity-30" data-testid="wizard-back"><ArrowLeft className="w-4 h-4" /> Back</button>
          <div className="text-center hidden sm:block"><p className="text-xs text-white/40 uppercase tracking-widest">Estimate</p><p className="text-gold font-serif text-xl">${total.toFixed(0)}</p></div>
          {step < totalSteps - 1 ? (
            <button onClick={() => go(1)} disabled={!canNext()} className="btn-gold !py-3 disabled:opacity-40" data-testid="wizard-next">Next <ArrowRight className="w-4 h-4" /></button>
          ) : <div className="w-24" />}
        </div>
      </div>
    </div>
  );
}
